import dayjs from 'dayjs';
import type { AlertTitle } from '@/api-client/alert/model/alert-title';
import type { AlertTargetItem } from '@/alert/components/alertTargetList/alertTargetList.types';
import type { RuleCriteria } from '@/alert/components/alertDetail/alertDetail.types';
import type { DetailItemInfo } from '@/common/components/organisms/alertListDetail/alertListDetail.types';
import type { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import type { EventAlertDetailInfo } from '@/alert/utils/types';
import { CHECK_BY_COMBINATION, CHECK_BY_TARGET } from '@/alert/utils/define';
import { getTzTimeNow, standardTimeToUtcZeroTime } from '@/common/utils/commonUtils';
import { buildTriggerTimeWindow } from '@/alert/utils/eventAlertTimeWindow.utils';
import type { AlertTreeRow } from '@/alert/components/alertGrid/alertGrid.uses';

export interface EventAlertOpenSource {
  resultId?: string;
  ruleId?: string;
  alertRuleId?: string;
  ruleName?: string;
  alertRuleName?: string;
  ruleCriteria?: string;
  alertRuleType?: string;
  level?: string;
  alertLevel?: string;
  eventType?: string;
  type?: string;
  targets?: AlertTargetItem[];
  targetList?: AlertTargetItem[];
  triggerTime?: string;
  collectTime?: string;
}

export type EventAlertDetailOpenParams = EventAlertOpenSource & {
  info?: EventAlertDetailInfo['info'];
  timePeriodInfo?: EventAlertDetailInfo['timePeriodInfo'];
  ruleType?: string;
  rawData?: Record<string, unknown>;
  targetId?: string;
  targetName?: string;
};

const toRuleCriteria = (value?: string): RuleCriteria =>
  value === CHECK_BY_COMBINATION ? CHECK_BY_COMBINATION : CHECK_BY_TARGET;

const getUnknownField = (value: unknown, key: string): unknown => {
  if (typeof value !== 'object' || value === null || !(key in value)) {
    return undefined;
  }
  return Reflect.get(value, key);
};

// 알람 target은 canonical(`{id,name,category}`), 서버형(`{targetId,targetName}`),
// row scalar(`targetId`) 형태로 들어올 수 있다.
const toAlertTargetItem = (item: unknown): AlertTargetItem | undefined => {
  const id = firstAlertField(getUnknownField(item, 'id'), getUnknownField(item, 'targetId'));
  if (!id) return undefined;

  const name =
    firstAlertField(getUnknownField(item, 'name'), getUnknownField(item, 'targetName')) ?? id;
  const category = toEventAlertFieldString(getUnknownField(item, 'category'));
  const alias = firstAlertField(
    getUnknownField(item, 'alias'),
    getUnknownField(item, 'targetAlias'),
  );

  return { id, name, ...(category && { category }), ...(alias && { alias }) };
};

const toAlertTargetItems = (value: unknown): AlertTargetItem[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const items = value.reduce<AlertTargetItem[]>((acc, item) => {
    const normalized = toAlertTargetItem(item);
    if (normalized) {
      acc.push(normalized);
    }
    return acc;
  }, []);
  return items.length ? items : undefined;
};

const buildSingleTargetFromRaw = (raw: Record<string, unknown>): AlertTargetItem[] | undefined => {
  const id = firstAlertField(raw.alertTargetId, raw.targetId);
  if (!id) return undefined;

  const name = firstAlertField(raw.targetName, raw.alertTargetName) ?? id;
  return [{ id, name }];
};

const resolveRuleNameFromInfo = (info?: EventAlertDetailInfo['info']): string | undefined =>
  toEventAlertFieldString(info?.[0]?.ruleName);

export const hasPrebuiltEventAlertDetailParams = (params: EventAlertDetailOpenParams): boolean =>
  Boolean(params.info && params.timePeriodInfo && params.ruleType);

// 여러 출처를 순서대로 보고 정규화 후 첫 유효 문자열을 고른다.
// `??`는 빈 문자열('')을 유효값으로 봐서 뒤 fallback을 막으므로 사용하지 않는다.
const firstAlertField = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    const normalized = toEventAlertFieldString(value);
    if (normalized) return normalized;
  }
  return undefined;
};

export const resolveEventAlertOpenSource = (
  input: EventAlertDetailOpenParams,
): EventAlertOpenSource => {
  const ruleNameFromInfo = resolveRuleNameFromInfo(input.info);
  const raw: Record<string, unknown> = input.rawData ?? {};

  return {
    resultId: firstAlertField(input.resultId, raw.resultId),
    // targetId는 도메인 값(alertRuleId/rawData)이 없을 때만 쓰는 마지막 fallback.
    ruleId: firstAlertField(input.ruleId, input.alertRuleId, raw.alertRuleId, input.targetId),
    alertRuleId: firstAlertField(input.alertRuleId, input.ruleId, raw.alertRuleId, input.targetId),
    ruleName: firstAlertField(
      input.ruleName,
      input.alertRuleName,
      raw.alertRuleName,
      input.targetName,
      ruleNameFromInfo,
    ),
    alertRuleName: firstAlertField(
      input.alertRuleName,
      input.ruleName,
      raw.alertRuleName,
      input.targetName,
      ruleNameFromInfo,
    ),
    ruleCriteria: firstAlertField(input.ruleCriteria, raw.ruleCriteria),
    alertRuleType: firstAlertField(input.alertRuleType, raw.alertRuleType),
    level: firstAlertField(input.level, input.alertLevel, raw.alertLevel),
    eventType: firstAlertField(input.eventType, raw.eventType),
    targets:
      (input.targets?.length ? input.targets : undefined) ??
      toAlertTargetItems(raw.targets) ??
      toAlertTargetItems(raw.targetItems) ??
      buildSingleTargetFromRaw(raw),
    triggerTime: firstAlertField(input.triggerTime, raw.triggerTime),
    collectTime: firstAlertField(input.collectTime, raw.collectTime),
  };
};

export const toEventAlertFieldString = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : undefined;
};

const resolveRuleId = (source: EventAlertOpenSource): string =>
  source.ruleId ?? source.alertRuleId ?? '';

const resolveRuleName = (source: EventAlertOpenSource): string =>
  source.ruleName ?? source.alertRuleName ?? '';

export const getEventAlertOpenMissingFields = (
  source: EventAlertOpenSource,
  options?: { info?: EventAlertDetailInfo['info'] },
): string[] => {
  const missing: string[] = [];
  if (!toEventAlertFieldString(source.resultId)) missing.push('resultId');
  if (!toEventAlertFieldString(resolveRuleId(source))) missing.push('ruleId');
  const ruleName =
    toEventAlertFieldString(resolveRuleName(source)) ?? resolveRuleNameFromInfo(options?.info);
  if (!ruleName) missing.push('ruleName');
  return missing;
};

export const buildEventAlertPanelName = (
  ruleName: string,
  ruleCriteria?: string,
  targets?: AlertTargetItem[],
): string => {
  const ruleType = toRuleCriteria(ruleCriteria);
  if (ruleType === CHECK_BY_TARGET && targets?.length) {
    return targets[0].alias || targets[0].name || ruleName;
  }
  return ruleName;
};

export const buildEventAlertTimePeriodInfo = (triggerTime?: string | null): TimePeriodInfo => {
  const now = getTzTimeNow();

  if (triggerTime) {
    const range = buildTriggerTimeWindow(triggerTime);
    const fromTime = dayjs(range.fromTimeUtc).format('YYYY-MM-DD HH:mm:ss');
    const toTime = dayjs(range.toTimeUtc).format('YYYY-MM-DD HH:mm:ss');
    return {
      timePeriod: range.period,
      fromTime,
      toTime,
      fromTimeUtc: range.fromTimeUtc,
      toTimeUtc: range.toTimeUtc,
      isPaused: true,
    };
  }

  const oneMonthAgo = now.subtract(1, 'month');
  const fromTime = oneMonthAgo.format('YYYY-MM-DD HH:mm:ss');
  const toTime = now.format('YYYY-MM-DD HH:mm:ss');
  return {
    timePeriod: 'pa',
    fromTime,
    toTime,
    fromTimeUtc: standardTimeToUtcZeroTime(fromTime),
    toTimeUtc: standardTimeToUtcZeroTime(toTime),
    isPaused: true,
  };
};

export const buildAlertTitleInfo = (source: EventAlertOpenSource): AlertTitle[] => {
  const ruleId = resolveRuleId(source);
  const ruleName = resolveRuleName(source);
  const targets = source.targets ?? source.targetList ?? [];

  const tags = targets.map((target) => ({
    tagId: target.id,
  }));

  return [
    {
      resultId: source.resultId,
      ruleId,
      ruleName,
      level: source.level ?? source.alertLevel,
      eventType: source.eventType,
      type: source.alertRuleType ?? source.type,
      tags,
    },
  ];
};

export const buildEventAlertDetailInfo = (
  source: EventAlertOpenSource,
): EventAlertDetailInfo | null => {
  const resultId = toEventAlertFieldString(source.resultId);
  const ruleId = toEventAlertFieldString(resolveRuleId(source));
  const ruleName = toEventAlertFieldString(resolveRuleName(source));

  if (!resultId || !ruleId || !ruleName) {
    return null;
  }

  const normalizedRuleType = toRuleCriteria(source.ruleCriteria);
  const triggerTime = source.triggerTime ?? source.collectTime;

  return {
    resultId,
    ruleId,
    info: buildAlertTitleInfo({
      ...source,
      resultId,
      ruleId,
      alertRuleId: ruleId,
      ruleName,
      alertRuleName: ruleName,
    }),
    ruleType: normalizedRuleType,
    timePeriodInfo: buildEventAlertTimePeriodInfo(triggerTime),
  };
};

export const buildEventAlertDetailInfoFromDetailItem = (
  item: DetailItemInfo,
): EventAlertDetailInfo | null =>
  buildEventAlertDetailInfo({
    resultId: item.resultId,
    ruleId: item.id,
    ruleName: item.name,
    ruleCriteria: item.ruleCriteria,
    alertRuleType: item.alertRuleType ?? item.type,
    level: item.level,
    eventType: item.eventType,
    targetList: item.targetList,
    // collectTime은 표시용(연도 생략)이라 시간창이 2001년으로 계산되므로, 연도 포함 triggerTime을 사용
    triggerTime: item.triggerTime,
  });

export const buildEventAlertDetailInfoFromAlertTreeRow = (
  row: AlertTreeRow,
): EventAlertDetailInfo | null => {
  return buildEventAlertDetailInfo({
    resultId: toEventAlertFieldString(row.resultId),
    alertRuleId: toEventAlertFieldString(row.alertRuleId),
    alertRuleName: toEventAlertFieldString(row.alertRuleName),
    ruleCriteria: toEventAlertFieldString(row.ruleCriteria),
    alertRuleType: toEventAlertFieldString(row.alertRuleType),
    alertLevel: toEventAlertFieldString(row.alertLevel),
    eventType: toEventAlertFieldString(getUnknownField(row, 'eventType')),
    targets: toAlertTargetItems(row.targets),
    triggerTime: toEventAlertFieldString(row.triggerTime),
  });
};
